import { useUIStore } from '@/config/stores/ui-store';
import { useActionStackStore } from '@/features/message-list/stores/action-stack';
import { DRAWER_WIDTH } from '@/layout';
import SearchField from '@/shared/components/search-field';
import { Box, Drawer as MuiDrawer, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageSearchItem, MessageSearchResponse } from '../../models/message-search-dto';
import { useMessageSearch } from '../../queries/message-search.queries';
import { MessageSearchDrawerHeader } from './message-search-drawer.header';
import { MessageSearchResults } from './message-search-drawer.results';

interface MessageSearchDrawerProps {
  conversationId: string | undefined;
}

export function MessageSearchDrawer({ conversationId }: MessageSearchDrawerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { messageSearchDrawerOpen, setMessageSearchDrawerOpen } = useUIStore();
  const { dispatchAction } = useActionStackStore();
  const [searchTerm, setSearchTerm] = useState('');

  // Search query - enabled when drawer open, term >= 3 chars, and conv selected
  const isSearchActive = messageSearchDrawerOpen && searchTerm.length >= 3 && !!conversationId;
  const {
    data: searchData,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    error
  } = useMessageSearch(conversationId, searchTerm, isSearchActive);

  // Flatten paginated results
  const searchItems = useMemo((): MessageSearchItem[] => {
    if (!searchData?.pages) return [];
    return searchData.pages.flatMap((page: MessageSearchResponse) => page.items) || [];
  }, [searchData?.pages]);

  const handleClose = useCallback(() => {
    setMessageSearchDrawerOpen(false);
    setSearchTerm('');
  }, [setMessageSearchDrawerOpen]);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleSelectResult = useCallback(
    (item: MessageSearchItem) => {
      if (!conversationId || !item.id) return;

      // Dispatch scroll action to message list
      dispatchAction({
        type: 'jumpTo',
        conversationId,
        targetMessageId: item.id
      });
      isMobile && handleClose();
    },
    [conversationId, dispatchAction, isMobile]
  );

  // Don't render if no conversation selected
  if (!conversationId) {
    return null;
  }

  return (
    <MuiDrawer
      anchor='left'
      variant='persistent'
      open={messageSearchDrawerOpen}
      onClose={handleClose}
      sx={{
        zIndex: theme.zIndex.drawer,
        '& .MuiDrawer-paper': {
          width: isMobile ? '100%' : DRAWER_WIDTH,
          maxWidth: '100vw',
          boxSizing: 'border-box'
        }
      }}
      ModalProps={{
        keepMounted: false
      }}
      aria-label={t('conversations.search.drawerLabel', 'Message search drawer')}
    >
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <MessageSearchDrawerHeader onClose={handleClose} />

        <Box sx={{ p: 2 }} component='div' className='mex:border-divider mex:border-t'>
          <SearchField
            value={searchTerm}
            onChange={handleSearch}
            delay={300}
            placeholder={t('conversations.search.placeholder', 'Search messages...')}
            autoFocus
            fullWidth
          />
        </Box>

        {/* Results area */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {/* Min chars message */}
          {searchTerm.length > 0 && searchTerm.length < 3 && (
            <Typography color='text.secondary' variant='body2' sx={{ py: 4, textAlign: 'center', px: 2 }}>
              {t('conversations.search.minChars', 'Type at least 3 characters')}
            </Typography>
          )}

          {/* Results */}
          {searchTerm.length >= 3 && (
            <MessageSearchResults
              searchTerm={searchTerm}
              items={searchItems}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetching={isFetching}
              isLoading={isLoading}
              error={error}
              onSelect={handleSelectResult}
            />
          )}
        </Box>
      </Box>
    </MuiDrawer>
  );
}
